/**
 * Admin Panel - Client-Side Validation Schemas
 *
 * Zod schemas for form validation (login, rejection, etc.)
 * Mirrors server-side schemas; used for instant client feedback.
 * File: src/lib/validations/admin-client.schema.ts
 */

import { z } from 'zod'

// ============================================================================
// LOGIN
// ============================================================================

export const adminLoginSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password required'),
})

export type AdminLoginInput = z.infer<typeof adminLoginSchema>

// ============================================================================
// REJECTION REASON
// ============================================================================

export const rejectionReasonSchema = z.object({
  rejectionReason: z
    .string()
    .trim()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must be under 500 characters'),
})

export type RejectionReasonInput = z.infer<typeof rejectionReasonSchema>

// ============================================================================
// INVITE
// ============================================================================

export const bulkInviteSchema = z.object({
  count: z.coerce
    .number()
    .int()
    .min(1, 'Enter a number between 1 and 100')
    .max(100, 'Enter a number between 1 and 100'),
  message: z
    .string()
    .max(500, 'Message must be under 500 characters')
    .optional(),
})

export type BulkInviteInput = z.infer<typeof bulkInviteSchema>

// ============================================================================
// ADMIN USER
// ============================================================================

export const createAdminSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter a valid email address'),
  role: z.enum(['admin', 'superadmin'], {
    message: 'Role must be admin or superadmin',
  }),
})

export type CreateAdminInput = z.infer<typeof createAdminSchema>
